<template>
  <main class="card-open-history-page">
    <AppNavBar
      :title="t('card.openCardHistory')"
      background-color="#ffffff"
      fallback-to="/wallet/open-card"
    />

    <nav
      class="card-open-history-tabs"
      :class="{ 'card-open-history-tabs--physical': isPhysicalCard }"
      :aria-label="t('card.openCardHistory')"
    >
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        :class="{ 'is-active': activeTab === tab.value }"
        @click="switchTab(tab.value)"
      >
        {{ tab.label }}
      </button>
    </nav>

    <van-skeleton
      v-if="loading"
      title
      :row="3"
      class="card-open-history-loading"
    />

    <section v-else-if="visibleRecords.length" class="card-open-history-list">
      <button
        v-for="item in visibleRecords"
        :key="item.id || item.cardId || item.createTime"
        type="button"
        class="card-open-history-item"
        @click="openDetail(item)"
      >
        <div class="card-open-history-item__main">
          <p>{{ recordTitle(item) }}</p>
          <span>
            {{ t("card.openCardHistoryAppliedAt")
            }}{{ formatCardTimestamp(recordTime(item)) }}
          </span>
        </div>
        <div
          class="card-open-history-item__summary"
          :class="`is-${recordStatus(item)}`"
        >
          <div>
            <strong>{{ recordAmount(item) }}</strong>
            <span>{{ recordStatusText(item) }}</span>
          </div>
          <van-icon name="arrow" size="16" />
        </div>
      </button>
    </section>

    <section v-else class="card-open-history-empty">
      <app-image :src="emptyImage" alt="" />
      <p>{{ t("card.openCardHistoryEmpty") }}</p>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { pcCardApplyList, pcCardPhysicalApplyList } from "@/api/card";
import emptyImage from "@/assets/images/empty.png";
import { useProfileStore } from "@/stores/profile";
import { getDeviceId } from "@/utils/device";
import { getRouteState, navigateWithState } from "@/utils/route-state";
import { formatCardTimestamp } from "@/view/card/utils";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const pageState = computed(() =>
  getRouteState(route, "card-open-history", {}),
);
const activeTab = ref("");
const loading = ref(false);
const records = ref([]);
const cardType = computed(() => Number(pageState.value?.cardType || 2));
const isPhysicalCard = computed(() => cardType.value === 1);
const tabs = computed(() => {
  if (isPhysicalCard.value) {
    return [
      { value: "", label: t("common.all") },
      {
        value: "pending",
        label: t("card.openCardHistoryPhysicalPendingStatus"),
      },
      {
        value: "activated",
        label: t("card.openCardHistoryPhysicalActivationStatus"),
      },
      {
        value: "fail",
        label: t("card.openCardHistoryPhysicalRejectedStatus"),
      },
    ];
  }

  return [
    { value: "", label: t("common.all") },
    { value: "CARD_CREATING", label: t("card.openCardHistoryPending") },
    { value: "SUCCESS", label: t("card.openCardHistoryApproved") },
    { value: "CANCELLED", label: t("card.openCardHistoryRejected") },
  ];
});
const visibleRecords = computed(() =>
  activeTab.value === ""
    ? records.value
    : records.value.filter(
        (item) => recordStatusTabValue(item) === activeTab.value,
      ),
);

function switchTab(value) {
  if (activeTab.value === value) {
    return;
  }
  activeTab.value = value;
  loadRecords();
}

function recordStatus(item) {
  const status = String(item?.status ?? "")
    .trim()
    .toLowerCase();

  if (
    (isPhysicalCard.value && status === "pending") ||
    (!isPhysicalCard.value && status === "card_creating")
  ) {
    return "pending";
  }
  if (
    (isPhysicalCard.value && status === "fail") ||
    (!isPhysicalCard.value &&
      ["failed", "cancelled", "canceled", "card_cancelled"].includes(status))
  ) {
    return "rejected";
  }
  if (
    isPhysicalCard.value &&
    ["succeed", "activated"].includes(status)
  ) {
    return "approved";
  }
  return "approved";
}

function recordStatusTabValue(item) {
  const status = recordStatus(item);
  if (isPhysicalCard.value) {
    if (status === "pending") return "pending";
    if (status === "rejected") return "fail";
    return "activated";
  }

  if (status === "pending") return "CARD_CREATING";
  if (status === "rejected") return "CANCELLED";
  return "SUCCESS";
}

function recordStatusText(item) {
  const status = recordStatus(item);
  if (isPhysicalCard.value) {
    if (status === "pending") {
      return t("card.openCardHistoryPhysicalPendingStatus");
    }
    if (status === "rejected") {
      return t("card.openCardHistoryPhysicalRejectedStatus");
    }
    return t("card.openCardHistoryPhysicalActivationStatus");
  }

  if (status === "pending") return t("card.openCardHistoryPending");
  if (status === "rejected") return t("card.openCardHistoryRejected");
  return t("card.openCardHistoryApproved");
}

function recordTitle(item) {
  const name =
    String(
      item?.name || item?.cardName || item?.templateName || item?.network || "",
    ).trim() || t("card.prepaidCard");
  return `${name} ${recordStatusText(item)}`;
}

function recordTime(item) {
  return item?.createTime || "";
}

function recordAmount(item) {
  const status = recordStatus(item);
  const amount = item?.providerPayAmount ?? "0";
  const symbol = String(item?.providerPaySymbol || "USDT").trim();
  const prefix = status === "approved" ? "-" : status === "rejected" ? "+" : "";
  return `${prefix}${amount} ${symbol}`.trim();
}

function openDetail(item) {
  navigateWithState(router, "/wallet/open-card/history/detail", {
    namespace: "card-open-history-detail",
    payload: {
      record: isPhysicalCard.value ? { ...item, physical: true } : item,
      status: recordStatus(item),
    },
  });
}

async function loadRecords() {
  if (!profileStore.platformAddress) {
    records.value = [];
    return;
  }

  loading.value = true;
  try {
    const applyListApi = isPhysicalCard.value
      ? pcCardPhysicalApplyList
      : pcCardApplyList;
    const result = await applyListApi(
      {
        address: profileStore.platformAddress,
        pageNum: 1,
        pageSize: 999,
        deviceToken: getDeviceId(),
        status: activeTab.value,
      },
      { meta: { showLoading: false } },
    );
    records.value = Array.isArray(result)
      ? result
      : Array.isArray(result?.list)
        ? result.list
        : [];
  } catch (_) {
    records.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(loadRecords);
</script>

<style scoped lang="scss">
.card-open-history-page {
  min-height: var(--app-viewport-height);
  background: #ffffff;
}

.card-open-history-tabs {
  display: flex;
  gap: 20px;
  padding: 12px 16px 0;

  button {
    position: relative;
    flex: 0 0 auto;
    padding: 0 0 9px;
    border: 0;
    background: transparent;
    color: #666666;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
  }

  button.is-active {
    color: #000000;
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;

    &::after {
      position: absolute;
      right: 50%;
      bottom: 0;
      width: 28px;
      height: 3px;
      border-radius: 3px;
      background: #3135ac;
      content: "";
      transform: translateX(50%);
    }
  }

  &--physical button.is-active {
    font-size: 16px;
  }
}

.card-open-history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 130px;
  color: #999999;
  font-size: 16px;
  line-height: 20px;

  .app-image {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }

  p {
    margin: 10px 0 0;
  }
}

.card-open-history-loading {
  margin: 24px 16px 0;
}

.card-open-history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
}

.card-open-history-item {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
  padding: 12px 8px 12px 16px;
  border: 0;
  border-radius: 16px;
  background: #f5f5f5;
  font: inherit;
  text-align: left;
}

.card-open-history-item__main {
  min-width: 0;

  p {
    margin: 0;
    overflow: hidden;
    color: #000000;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 10px;
    color: #999999;
    font-size: 12px;
    line-height: 12px;
  }
}

.card-open-history-item__summary {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
  text-align: right;

  strong {
    display: block;
    color: #ef0027;
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
  }

  span {
    display: block;
    margin-top: 10px;
    color: #999999;
    font-size: 12px;
    font-weight: 500;
    line-height: 12px;
  }

  .van-icon {
    color: #999999;
  }

  &.is-pending {
    strong,
    span {
      color: #2667ff;
    }
  }

  &.is-rejected {
    strong {
      color: #07b077;
    }

    span {
      color: #ef0027;
    }
  }
}
</style>
